import { Actions } from '@store/reducer.ts'
import { useAppContext } from '@hooks/use-app-context.ts'
import { useApi } from '@hooks/use-api-requests.hook.ts'
import React, { useState } from 'react'
//import { UploadError } from '@ui/stepper/steps/richiesta/components/modal-views/hooks/use-upload.hook.ts'
import { getError, UPLOAD_ERROR_TEMPLATES } from '@utils/validators.ts'
import { calculateCAP } from '@utils/validation-net'
import { log } from '@utils/debug'
import type { UploadError } from '@ui/richiesta/pages/components/use-upload.hook'

export type UserDocument = 'IDE' | 'CF' | 'ALTRO' | 'X'
export type OCRDocument =
	| 'HEALTH INSURANCE CARD'
	| 'IDENTITY CARD'
	| 'X'
	| 'ALTRO'
	| 'DRIVER LICENSE'

type OCRValue = {
	'raw-value'?: string
	value: string
}

type OCRValidation = {
	description: string
	passed: boolean
	'error-code': [OCRErrorCodes, string] // [code, description]
}

type OCRFISCALBODY = Partial<{
	BIRTHDATE: OCRValue
	NAME: OCRValue
	SURNAME: OCRValue
	SEX: OCRValue
	BIRTHPLACE: OCRValue
	'TAX NUMBER': OCRValue
	ADDRESS: OCRValue
	NATIONALITY: OCRValue
	'ISSUE PLACE': OCRValue
	'ISSUE DATE': OCRValue
	'ID NUMBER': OCRValue
	NUMBER: OCRValue
}>

type OCRResponse = {
	confidence: boolean
	errors: Record<string, unknown> | null
	documents: Array<{
		'document-type': OCRDocument
		'document-data': OCRFISCALBODY
		validations: Array<OCRValidation>
	}>
	CapRilascio: null | string
	ComuneCodRilascio: null | string
	ComuneRilascio: null | string
	ProvinciaRilascio: null | string
	CIE: boolean
	invalidDocument: boolean
}

type OCRErrorCodes = 'C0101' | 'B0101'

const CARD_TYPE: Record<UserDocument, OCRDocument> = {
	IDE: 'IDENTITY CARD',
	CF: 'HEALTH INSURANCE CARD',
	X: 'X',
	ALTRO: 'ALTRO',
}

function useOCR(id: UserDocument) {
	const { state, dispatch } = useAppContext()
	const [completeStatus, setComplete] = useState<boolean | null>(null)
	const [error, setError] = useState<UploadError>({
		type: 'NONE',
		description: UPLOAD_ERROR_TEMPLATES['NONE'].description,
		title: UPLOAD_ERROR_TEMPLATES['NONE'].title,
	})

	const [isCIE, setCIE] = useState(false)

	const [OCRData, fetchOCR] = useApi<OCRResponse>({
		request: 'InfoCert/InfoCertOCR',
		method: 'POST',
		sleeper: true,
		silent: true,
		file: true,
		shouldCache: false,
	})

	const findErrorCode = (
		validations: Array<OCRValidation>,
		errorCode: OCRErrorCodes
	) =>
		validations.find(
			(validation) =>
				validation['error-code'][0] === errorCode && !validation.passed
		)

	React.useEffect(() => {
		setComplete(null)

		if (OCRData && OCRData.invalidDocument) {
			setError(getError('INVALID_DOCUMENT'))
			setComplete(false)
		} else if (OCRData && !OCRData.confidence) {
			setError(getError('CONFIDENCE_LOW'))
			setComplete(false)
		} else if (OCRData && OCRData.errors !== null) {
			setError(getError('OCR'))
			setComplete(false)
		} else if (OCRData && OCRData.documents !== null) {
			if (!isCIE) {
				// lets not override if is set to true
				setCIE(OCRData.CIE)
				dispatch({
					type: Actions.SetCIE,
					payload: OCRData.CIE,
				})
			}

			OCRData?.documents.forEach((document) => {
				const data = document['document-data']
				const expiredDocument = findErrorCode(document.validations, 'C0101')
				const invalidDocument = findErrorCode(document.validations, 'B0101')

				if (expiredDocument) {
					setError(getError('DOCUMENT_EXPIRED'))
					setComplete(false)

					return
				} else if (invalidDocument) {
					setError(getError('DOCUMENT_INVALID'))
					setComplete(false)

					return
				} else if (
					document['document-type'] === CARD_TYPE[id] ||
					(document['document-type'] === 'DRIVER LICENSE' &&
						CARD_TYPE[id] === 'IDENTITY CARD')
				) {
					switch (document['document-type']) {
						case 'HEALTH INSURANCE CARD':
							dispatch({
								type: Actions.UpdateData,
								payload: {
									...state.data,
									Two: {
										...state.data.Two,
										BirthLocation: '',
										BirthDate: '',
										BirthLocationId: '',
										BirthStateId: '',
										Name: document['document-data'].NAME?.value || '',
										Surname: document['document-data'].SURNAME?.value || '',
										Gender: document['document-data'].SEX?.value || '',
										FiscalCode:
											document['document-data']['TAX NUMBER']?.value || '',
									},
								},
							})
							break
						case 'IDENTITY CARD':
							log('ocr-id', data, data['TAX NUMBER'])
							dispatch({
								type: Actions.UpdateData,
								payload: {
									...state.data,
									Two: {
										...state.data.Two,
										Name: data.NAME?.value || '',
										Surname: data.SURNAME?.value || '',
										Gender: data.SEX?.value || '',
										BirthLocation: '',
										BirthDate: '',
										BirthStateId: '',
										BirthLocationId: '',
										FiscalCode: OCRData.CIE
											? (data['TAX NUMBER']?.value ?? '')
											: state.data.Two.FiscalCode,
									},
									Three: {
										...state.data.Three,
										Citizenship: (() => { const nat = data.NATIONALITY?.value?.trim().toUpperCase(); return nat === 'ITA' || nat === 'IT' ? 'IT' : ''; })(),
										CurrentAddress: {
											...state.data.Three.CurrentAddress,
											Address: data.ADDRESS?.value || '',
											ComuneLabel: data['ISSUE PLACE']?.value || '',
											Comune: OCRData.ComuneCodRilascio || '',
											Provincia: OCRData.ProvinciaRilascio || '',
											CAP: calculateCAP(OCRData.CapRilascio),
										},
										Document: {
											...state.data.Three.Document,
											DocumentType: 'IDE',
											Serial:
												data['ID NUMBER']?.value || data['NUMBER']?.value || '',
											DateOfIssue: data['ISSUE DATE']?.value || '',
											Address: {
												...state.data.Three.Document.Address,
												Address: data.ADDRESS?.value || '',
												ComuneLabel: data['ISSUE PLACE']?.value || '',
												Comune: OCRData.ComuneCodRilascio || '',
												Provincia: OCRData.ProvinciaRilascio || '',
												CAP: calculateCAP(OCRData.CapRilascio),
											},
										},
									},
								},
							})
							break
						case 'DRIVER LICENSE':
							dispatch({
								type: Actions.UpdateData,
								payload: {
									...state.data,
									Two: {
										...state.data.Two,
										Name: data.NAME?.value || '',
										Surname: data.SURNAME?.value || '',
										BirthLocation: '',
										BirthDate: '',
										BirthLocationId: '',
										BirthStateId: '',
										Gender: data.SEX?.value || '',
										FiscalCode: OCRData.CIE
											? (data['TAX NUMBER']?.value ?? '')
											: state.data.Two.FiscalCode,
									},
									Three: {
										...state.data.Three,
										Citizenship: (() => { const nat = data.NATIONALITY?.value?.trim().toUpperCase(); return nat === 'ITA' || nat === 'IT' ? 'IT' : ''; })(),
										Document: {
											...state.data.Three.Document,
											DocumentType: 'PAT',
											Serial: data['NUMBER']?.value || '',
											DateOfIssue: data['ISSUE DATE']?.value || '',
											Address: {
												...state.data.Three.Document.Address,
												Address: data.ADDRESS?.value || '',
												ComuneLabel: data['ISSUE PLACE']?.value || '',
												Comune: OCRData.ComuneCodRilascio || '',
												Provincia: OCRData.ProvinciaRilascio || '',
												CAP: calculateCAP(OCRData.CapRilascio),
											},
										},
									},
								},
							})
							break
						default:
							break
					}

					setComplete(true)
				} else {
					setError(getError('WRONG_DOCUMENT')) // throw error if id doesnt match
					setComplete(false)
				}
			})
		}
	}, [OCRData])

	return {
		fetchOCR,
		completeStatus,
		error,
		setError,
		setComplete,
		isCIE,
	}
}

export { type OCRResponse, type OCRDocument, useOCR }
